import React, { useState, useEffect, useCallback } from "react";
import { useI18n } from "../lib/i18n";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ComposedChart, Area } from "recharts";
import { BarChart3, TrendingUp, Users, Activity, ChevronDown, ChevronUp, Brain, Sparkles, ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const COLORS = ["#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd", "#818cf8", "#7c3aed", "#5b21b6", "#4f46e5"];
const ACCENT = "#22d3ee";

const StatCard = ({ label, value, sub, testId, ...rest }) => {
  const Icon = rest.icon;
  return (
  <Card className="bg-card/50 border-border/40" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="p-2 rounded-lg bg-primary/10"><Icon className="h-5 w-5 text-primary" /></div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-bold text-foreground">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-2 shadow-lg text-xs">
      <p className="font-medium text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <span className="font-bold">{p.value}</span></p>
      ))}
    </div>
  );
};

const PatientTroubleManager = ({ patients, troubleOptions, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAddTrouble = async (patientId, trouble) => {
    const patient = patients.find(p => p.user_id === patientId);
    if (!patient) return;
    const current = patient.troubles || [];
    if (current.includes(trouble)) return;
    const updated = [...current, trouble];
    setSaving(true);
    try {
      await axios.put(`${API}/therapist/patients/${patientId}/profile`, { troubles: updated }, { withCredentials: true });
      onUpdate();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
    setEditing(null);
  };

  const handleRemoveTrouble = async (patientId, trouble) => {
    const patient = patients.find(p => p.user_id === patientId);
    if (!patient) return;
    const updated = (patient.troubles || []).filter(t => t !== trouble);
    setSaving(true);
    try {
      await axios.put(`${API}/therapist/patients/${patientId}/profile`, { troubles: updated }, { withCredentials: true });
      onUpdate();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  return (
    <Card className="bg-card/50 border-border/40" data-testid="patient-trouble-manager">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Diagnostics patients ({patients.length})</span>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 space-y-2 max-h-[300px] overflow-y-auto">
          {patients.map(p => (
            <div key={p.user_id} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0">
              <span className="text-sm text-foreground truncate max-w-[140px]">{p.name}</span>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                {(p.troubles || []).map(t => (
                  <Badge key={t} variant="secondary" className="text-[10px] cursor-pointer hover:bg-destructive/20" onClick={() => handleRemoveTrouble(p.user_id, t)}>
                    {t} x
                  </Badge>
                ))}
                {editing === p.user_id ? (
                  <Select onValueChange={(v) => handleAddTrouble(p.user_id, v)} disabled={saving}>
                    <SelectTrigger className="h-6 w-[140px] text-[10px]"><SelectValue placeholder="Ajouter..." /></SelectTrigger>
                    <SelectContent>
                      {troubleOptions.filter(t => !(p.troubles || []).includes(t)).map(t => (
                        <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="text-[10px] cursor-pointer hover:bg-primary/10" onClick={() => setEditing(p.user_id)} data-testid={`add-trouble-${p.user_id}`}>+</Badge>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};

export default function StatisticsPage({ user, logout }) {
  const { t } = useI18n();
  const [trouble, setTrouble] = useState("all");
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [troubleOptions, setTroubleOptions] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [dersStats, setDersStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { months };
      if (trouble !== "all") params.trouble = trouble;
      const [statsRes, optionsRes, patientsRes, dersRes] = await Promise.all([
        axios.get(`${API}/therapist/statistics/emotion-regulation`, { params, withCredentials: true }),
        axios.get(`${API}/therapist/statistics/trouble-options`, { withCredentials: true }),
        axios.get(`${API}/therapist/patients`, { withCredentials: true }),
        axios.get(`${API}/ders/stats`, { withCredentials: true }).catch(() => ({ data: { results: [] } })),
      ]);
      setData(statsRes.data);
      setTroubleOptions(optionsRes.data);
      setAllPatients(patientsRes.data);
      setDersStats(dersRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [trouble, months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const s = data?.summary || {};

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-6xl mx-auto space-y-6 pb-12" data-testid="statistics-page">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Statistiques</h1>
          <p className="text-sm text-muted-foreground mt-1">Regulation emotionnelle — Adherence aux exercices</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="stats-filters">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Trouble :</span>
            <Select value={trouble} onValueChange={setTrouble}>
              <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="trouble-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les patients</SelectItem>
                {troubleOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Periode :</span>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="period-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 mois</SelectItem>
                <SelectItem value="6">6 mois</SelectItem>
                <SelectItem value="12">12 mois</SelectItem>
                <SelectItem value="24">24 mois</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
        ) : !data ? (
          <p className="text-muted-foreground text-center py-10">Aucune donnee disponible</p>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard icon={Users} label="Patients" value={s.total_patients || 0} sub={trouble !== "all" ? trouble : undefined} testId="stat-patients" />
              <StatCard icon={Activity} label="Entrees emotions" value={s.total_emotion_entries || 0} sub={`+ ${s.total_mood_entries || 0} humeur`} testId="stat-entries" />
              <StatCard icon={BarChart3} label="Taux completion" value={`${s.avg_completion_rate || 0}%`} sub={`${s.total_completed || 0}/${s.total_assigned || 0}`} testId="stat-completion" />
              <StatCard icon={TrendingUp} label="Patients actifs" value={s.patients_with_emotion_entries || 0} sub={`sur ${s.total_patients || 0}`} testId="stat-active" />
              <StatCard icon={Brain} label="Delta moyen" value={`${s.avg_delta || 0} pts`} sub="Reduction intensite" testId="stat-delta" />
              <StatCard icon={Sparkles} label="Score composite" value={`${s.avg_composite || 0}/10`} sub="Regulation emotionnelle" testId="stat-composite" />
            </div>

            {/* Patient trouble manager */}
            <PatientTroubleManager patients={allPatients} troubleOptions={troubleOptions} onUpdate={fetchData} />

            {/* NEW: Composite Emotion Regulation Score */}
            {data.composite?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-composite">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Score composite de regulation emotionnelle
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground mt-1">Ponderation : Delta 50% + Diversite emotionnelle 25% + Frequence 25%</p>
                </CardHeader>
                <CardContent>
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={data.composite} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis dataKey="patient" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-30} textAnchor="end" interval={0} />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs space-y-1">
                              <p className="font-bold text-foreground">{label}</p>
                              <p className="text-primary font-bold">Score composite : {d?.composite}/10</p>
                              <hr className="border-border/30" />
                              <p>Delta intensite : {d?.delta_score}/10 <span className="text-muted-foreground">({d?.delta_count} mesures)</span></p>
                              <p>Diversite emotionnelle : {d?.diversity_score}/10 <span className="text-muted-foreground">({d?.unique_emotions} emotions)</span></p>
                              <p>Frequence utilisation : {d?.frequency_score}/10 <span className="text-muted-foreground">({d?.entries_per_month}/mois)</span></p>
                            </div>
                          );
                        }} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="delta_score" name="Delta (50%)" fill="#6366f1" stackId="a" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="diversity_score" name="Diversite (25%)" fill="#8b5cf6" stackId="b" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="frequency_score" name="Frequence (25%)" fill="#a78bfa" stackId="c" radius={[0, 0, 0, 0]} />
                        <Line type="monotone" dataKey="composite" name="Score composite" stroke="#22d3ee" strokeWidth={2.5} dot={{ fill: "#22d3ee", r: 5 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* NEW: Delta intensity per patient */}
            {data.delta_per_patient?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-delta-patient">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Brain className="h-4 w-4 text-primary" />
                    Delta moyen d'intensite emotionnelle par patient
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground mt-1">Reduction d'intensite (avant - apres) : restructuration cognitive, registre emotions, registre pensees, defusion cognitive</p>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.delta_per_patient} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis dataKey="patient" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-30} textAnchor="end" interval={0} />
                        <YAxis domain={[0, 'auto']} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} label={{ value: "pts", position: "insideTopLeft", fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <div className="bg-popover border border-border rounded-lg p-2 shadow-lg text-xs space-y-0.5">
                              <p className="font-bold text-foreground">{label}</p>
                              <p>Delta moyen : <span className="font-bold text-emerald-400">{d?.avg_delta} pts</span></p>
                              <p>Mesures : {d?.count}</p>
                              <p>Min : {d?.min_delta} | Max : {d?.max_delta}</p>
                            </div>
                          );
                        }} />
                        <Bar dataKey="avg_delta" name="Delta moyen (pts)" radius={[4, 4, 0, 0]}>
                          {data.delta_per_patient.map((d, i) => (
                            <Cell key={i} fill={d.avg_delta >= 3 ? "#10b981" : d.avg_delta >= 1 ? "#f59e0b" : "#ef4444"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex gap-4 justify-center mt-2 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Bonne regulation (3+)</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Moderee (1-3)</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Faible (&lt;1)</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* NEW: Delta evolution over time */}
            {data.delta_timeline?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-delta-timeline">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    Evolution du delta d'intensite dans le temps
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground mt-1">Delta moyen par mois (tous patients confondus) — un delta croissant indique une meilleure regulation</p>
                </CardHeader>
                <CardContent>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={data.delta_timeline} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} label={{ value: "pts", position: "insideTopLeft", fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <div className="bg-popover border border-border rounded-lg p-2 shadow-lg text-xs">
                              <p className="font-bold">{label}</p>
                              <p>Delta moyen : <span className="text-emerald-400 font-bold">{d?.avg_delta} pts</span></p>
                              <p className="text-muted-foreground">{d?.count} mesures</p>
                            </div>
                          );
                        }} />
                        <Area type="monotone" dataKey="avg_delta" name="Delta" fill="#6366f1" fillOpacity={0.15} stroke="transparent" />
                        <Line type="monotone" dataKey="avg_delta" name="Delta moyen" stroke="#22d3ee" strokeWidth={2.5} dot={{ fill: "#22d3ee", r: 4 }} activeDot={{ r: 6 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chart 1: Volume per patient */}
            {data.volume?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-volume">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">A. Registre des emotions — Nombre d'entrees par patient</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.volume} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis dataKey="patient" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-30} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="entries" name="Entrees" radius={[4, 4, 0, 0]}>
                          {data.volume.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chart 2: Completion rate per patient */}
            {data.completion?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-completion">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">B. Taux de completion — Exercices emotionnels assignes vs completes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.completion} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis dataKey="patient" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-30} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="assigned" name="Assignes" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="completed" name="Completes" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chart 3: Frequency over time */}
            {data.frequency?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-frequency">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">C. Frequence — Entrees registre des emotions par mois</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.frequency} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip content={<ChartTooltip />} />
                        <Line type="monotone" dataKey="entries" name="Entrees" stroke={ACCENT} strokeWidth={2} dot={{ fill: ACCENT, r: 4 }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mood tracking volume */}
            {data.mood_volume?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-mood">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">D. Suivi de l'humeur — Nombre d'entrees par patient</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.mood_volume} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis dataKey="patient" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-30} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="entries" name="Entrees humeur" radius={[4, 4, 0, 0]}>
                          {data.mood_volume.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Psychoeducation */}
            {data.psychoed?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-psychoed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">E. Psychoeducation emotions — Modules assignes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {data.psychoed.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
                        <span className="text-sm text-foreground">{p.patient}</span>
                        <div className="flex gap-1">
                          {p.modules.map(m => <Badge key={m} variant="secondary" className="text-[10px]">{m.replace("_", " ")}</Badge>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* DERS Questionnaire Summary */}
            {dersStats?.results?.length > 0 && (
              <Card className="bg-card/50 border-border/40" data-testid="chart-ders">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-primary" />
                    F. Questionnaire DERS — Resultats par patient
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground mt-1">Echelle des Difficultes de Regulation Emotionnelle (pre/post psychoeducation). Un delta positif indique une amelioration.</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dersStats.results.map((r, i) => (
                      <div key={r.patient_id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <Link to={`/therapist/patients/${r.patient_id}/ders`} className="text-sm font-medium text-foreground hover:text-primary transition-colors truncate">
                            {r.patient}
                          </Link>
                          {(r.troubles || []).map(t => (
                            <Badge key={t} variant="secondary" className="text-[9px] hidden sm:inline">{t}</Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {r.has_pre && (
                            <div className="text-center">
                              <p className="text-[9px] text-muted-foreground">Pre</p>
                              <p className="text-sm font-bold">{r.pre_total}</p>
                            </div>
                          )}
                          {r.has_post && (
                            <div className="text-center">
                              <p className="text-[9px] text-muted-foreground">Post</p>
                              <p className="text-sm font-bold">{r.post_total}</p>
                            </div>
                          )}
                          {r.delta !== undefined && (
                            <Badge variant="default" className={`text-[10px] ${r.improved ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`}>
                              {r.improved ? "-" : "+"}{Math.abs(r.delta)}
                            </Badge>
                          )}
                          {!r.has_pre && !r.has_post && (
                            <span className="text-xs text-muted-foreground">{r.total_assessments} eval.</span>
                          )}
                          <Link to={`/therapist/patients/${r.patient_id}/ders`} className="text-xs text-primary hover:underline" data-testid={`ders-detail-${r.patient_id}`}>
                            Detail
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No data state */}
            {!data.volume?.length && !data.completion?.length && !data.frequency?.length && (
              <Card className="bg-card/50 border-border/40">
                <CardContent className="py-10 text-center">
                  <p className="text-muted-foreground">Aucune donnee pour les criteres selectionnes.</p>
                  <p className="text-xs text-muted-foreground mt-1">Assignez des diagnostics à vos patients et des exercices emotionnels.</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
